import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

type HomeSize = "studio" | "medium" | "large" | "business";
type Frequency = "rare" | "occasional" | "frequent" | "medical";
type Budget = "budget" | "mid" | "premium" | "enterprise";

interface QuizAnswers {
  homeSize: HomeSize | null;
  frequency: Frequency | null;
  budget: Budget | null;
}

interface Recommendation {
  title: string;
  productType: string;
  category: string;
  priceBucket: string;
  brands: string[];
  desc: string;
  ctaLabel: string;
  icon: string;
  accentColor: string;
}

function getRecommendation(
  homeSize: HomeSize,
  frequency: Frequency,
  budget: Budget
): Recommendation {
  if (frequency === "medical") {
    if (budget === "enterprise" || homeSize === "large" || homeSize === "business") {
      return {
        title: "Whole-Home Standby System",
        productType: "Automatic Standby Generator",
        category: "whole-home",
        priceBucket: budget === "enterprise" ? "over5000" : "2500to5000",
        brands: ["Generac"],
        desc: "For medical-critical needs, an automatic standby system powers your entire home within 10 seconds of an outage — fully automatic, no manual startup, no missed doses.",
        ctaLabel: "Shop Standby Systems",
        icon: "ri-heart-pulse-line",
        accentColor: "border-red-400",
      };
    }
    return {
      title: "High-Capacity LiFePO4 Power Station",
      productType: "LiFePO4 Power Station",
      category: "solar",
      priceBucket: budget === "mid" ? "500to1000" : "1000to2500",
      brands: ["EcoFlow", "Bluetti"],
      desc: "A LiFePO4 power station runs medical devices silently and safely indoors — no fumes, no noise — and recharges from solar panels even during extended grid outages.",
      ctaLabel: "Shop Power Stations",
      icon: "ri-battery-charge-line",
      accentColor: "border-amber-400",
    };
  }

  if (
    (homeSize === "large" || homeSize === "business") &&
    frequency === "frequent" &&
    (budget === "premium" || budget === "enterprise")
  ) {
    return {
      title: "Whole-Home Standby System",
      productType: "Automatic Standby Generator",
      category: "whole-home",
      priceBucket: budget === "enterprise" ? "over5000" : "2500to5000",
      brands: ["Generac", "Honda"],
      desc: "Permanently installed, automatically kicks on within 10 seconds. Protects your HVAC, refrigerators, security systems, and everything else — without you lifting a finger.",
      ctaLabel: "Shop Whole-Home Systems",
      icon: "ri-home-gear-line",
      accentColor: "border-emerald-400",
    };
  }

  if (homeSize === "large" || homeSize === "business") {
    if (budget === "enterprise") {
      return {
        title: "Whole-Home or High-Output System",
        productType: "Standby or Portable Generator",
        category: "whole-home",
        priceBucket: "over5000",
        brands: ["Generac", "Honda"],
        desc: "At your budget, a full standby install gives you seamless automatic backup for the entire home or business — protecting everything from HVAC to refrigerators.",
        ctaLabel: "Shop Whole-Home Systems",
        icon: "ri-community-line",
        accentColor: "border-emerald-400",
      };
    }
    return {
      title: "High-Output Portable Generator",
      productType: "Gas Generator",
      category: "gas",
      priceBucket: budget === "premium" ? "1000to2500" : "500to1000",
      brands: ["Honda", "Generac"],
      desc: "A high-output inverter generator keeps your major appliances running during extended outages — reliable, powerful, and quiet enough to run near windows.",
      ctaLabel: "Shop Gas Generators",
      icon: "ri-gas-station-line",
      accentColor: "border-stone-400",
    };
  }

  if (homeSize === "medium" && frequency === "frequent") {
    if (budget === "premium" || budget === "enterprise") {
      return {
        title: "Expandable Solar Generator System",
        productType: "Solar Generator + Expansion Batteries",
        category: "solar",
        priceBucket: budget === "enterprise" ? "2500to5000" : "1000to2500",
        brands: ["EcoFlow", "Bluetti"],
        desc: "An expandable solar system with extra battery packs gives you days of silent, clean backup power — and recharges itself from solar panels even during prolonged grid outages.",
        ctaLabel: "Shop Solar Systems",
        icon: "ri-sun-line",
        accentColor: "border-amber-400",
      };
    }
    return {
      title: "Mid-Range Portable Power Station",
      productType: "Portable Power Station",
      category: "solar",
      priceBucket: "500to1000",
      brands: ["EcoFlow", "Jackery"],
      desc: "A mid-range portable station handles your essentials — fridge, lights, router, and devices — for 12–24+ hours per charge, quiet and safe to run indoors.",
      ctaLabel: "Shop Power Stations",
      icon: "ri-flashlight-line",
      accentColor: "border-amber-400",
    };
  }

  if (homeSize === "studio" || (homeSize === "medium" && frequency !== "frequent")) {
    if (budget === "budget") {
      return {
        title: "Compact Portable Power Station",
        productType: "Entry-Level Power Station",
        category: "solar",
        priceBucket: "under500",
        brands: ["Jackery", "Goal Zero", "EcoFlow"],
        desc: "A compact station keeps your phone, laptop, router, and essential lights powered during short outages — fits on a shelf and charges from any outlet or solar panel.",
        ctaLabel: "Shop Compact Stations",
        icon: "ri-battery-2-charge-line",
        accentColor: "border-amber-400",
      };
    }
    if (budget === "enterprise") {
      return {
        title: "Premium Solar Generator System",
        productType: "Solar Generator + Panel Array",
        category: "solar",
        priceBucket: "2500to5000",
        brands: ["EcoFlow", "Bluetti"],
        desc: "A premium solar setup with panel array gives you near off-grid independence — run your home on clean solar power and store enough for overnight and cloudy days.",
        ctaLabel: "Shop Premium Solar",
        icon: "ri-sun-cloudy-line",
        accentColor: "border-amber-400",
      };
    }
  }

  if (budget === "budget") {
    return {
      title: "Entry-Level Power Station",
      productType: "Portable Power Station",
      category: "solar",
      priceBucket: "under500",
      brands: ["Jackery", "Goal Zero"],
      desc: "A reliable entry-level station is perfect for keeping your essentials charged during occasional short outages without breaking the bank.",
      ctaLabel: "Shop Power Stations",
      icon: "ri-battery-2-charge-line",
      accentColor: "border-amber-400",
    };
  }

  if (budget === "premium") {
    return {
      title: "Solar Generator",
      productType: "Mid-Range Solar Generator",
      category: "solar",
      priceBucket: "1000to2500",
      brands: ["EcoFlow", "Jackery", "Bluetti"],
      desc: "A mid-range solar generator strikes the perfect balance between capacity and portability — enough power for your key appliances with solar recharging built in.",
      ctaLabel: "Shop Solar Generators",
      icon: "ri-sun-line",
      accentColor: "border-amber-400",
    };
  }

  return {
    title: "Mid-Range Portable Power Station",
    productType: "Portable Power Station",
    category: "solar",
    priceBucket: "500to1000",
    brands: ["EcoFlow", "Jackery", "Bluetti"],
    desc: "A mid-range power station handles your essentials — fridge, lights, router, and devices — with enough capacity for overnight backup and solar recharging support.",
    ctaLabel: "Shop Power Stations",
    icon: "ri-flashlight-line",
    accentColor: "border-amber-400",
  };
}

const steps = [
  {
    id: 1,
    key: "homeSize" as const,
    eyebrow: "Step 1 of 3 — Space",
    question: "What are you protecting?",
    sub: "Choose the option that best describes your space and power needs.",
    options: [
      { value: "studio", label: "Apartment or Studio", sub: "Small space, essential devices", icon: "ri-building-line" },
      { value: "medium", label: "2–3 Bedroom Home", sub: "Average family home", icon: "ri-home-2-line" },
      { value: "large", label: "4+ Bedroom / Large Home", sub: "Whole-home coverage needed", icon: "ri-community-line" },
      { value: "business", label: "Small Business / Workshop", sub: "Commercial or critical equipment", icon: "ri-store-line" },
    ],
  },
  {
    id: 2,
    key: "frequency" as const,
    eyebrow: "Step 2 of 3 — Reliability",
    question: "How often does your power go out?",
    sub: "This shapes what type and capacity of system you need.",
    options: [
      { value: "rare", label: "Rarely — Peace of Mind", sub: "Just want a safety net", icon: "ri-shield-line" },
      { value: "occasional", label: "A Few Times Per Year", sub: "Seasonal storms, occasional grid issues", icon: "ri-calendar-event-line" },
      { value: "frequent", label: "Monthly or More", sub: "Unreliable grid, rural area", icon: "ri-thunderstorms-line" },
      { value: "medical", label: "We Depend on Power", sub: "Medical devices or critical systems", icon: "ri-heart-pulse-line" },
    ],
  },
  {
    id: 3,
    key: "budget" as const,
    eyebrow: "Step 3 of 3 — Budget",
    question: "What\'s your investment range?",
    sub: "More budget unlocks more capacity, more automation, and longer runtime.",
    options: [
      { value: "budget", label: "Under $500", sub: "Essential coverage, great value", icon: "ri-money-dollar-circle-line" },
      { value: "mid", label: "$500 – $1,500", sub: "Solid capacity for most homes", icon: "ri-coin-line" },
      { value: "premium", label: "$1,500 – $5,000", sub: "Expandable solar systems", icon: "ri-bank-card-line" },
      { value: "enterprise", label: "$5,000+", sub: "Whole-home or off-grid setups", icon: "ri-home-gear-line" },
    ],
  },
];

export default function SystemQuiz() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = intro, 1-3 = questions, 4 = result
  const [answers, setAnswers] = useState<QuizAnswers>({ homeSize: null, frequency: null, budget: null });
  const [animating, setAnimating] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    const el = document.getElementById("system-quiz-section");
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const currentStep = steps[step - 1];

  const handleAnswer = (key: keyof QuizAnswers, value: string) => {
    if (animating) return;
    const updated = { ...answers, [key]: value };
    setAnswers(updated);
    setAnimating(true);
    setTimeout(() => {
      setStep((s) => s + 1);
      setAnimating(false);
    }, 280);
  };

  const handleBack = () => {
    if (step > 1) {
      setAnimating(true);
      setTimeout(() => {
        setStep((s) => s - 1);
        setAnimating(false);
      }, 180);
    } else {
      setStep(0);
    }
  };

  const handleShop = (rec: Recommendation) => {
    navigate(`/shop?category=${rec.category}&price=${rec.priceBucket}`);
  };

  const recommendation =
    answers.homeSize && answers.frequency && answers.budget
      ? getRecommendation(answers.homeSize, answers.frequency, answers.budget)
      : null;

  return (
    <section
      id="system-quiz-section"
      className={`py-20 bg-gray-950 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Eyebrow */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-bold px-3 py-1 rounded-full mb-4">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-flashlight-fill"></i>
            </div>
            2-Minute System Match
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight">
            Build Your Backup System
          </h2>
          <p className="text-gray-400 text-sm mt-3 max-w-xl mx-auto leading-relaxed">
            Answer 3 quick questions and we&apos;ll match you to the right product category, capacity, and price range — no guesswork.
          </p>
        </div>

        {/* Progress bar */}
        {step >= 1 && step <= 3 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {steps.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      step > i + 1
                        ? "bg-amber-500 text-white"
                        : step === i + 1
                        ? "bg-amber-500 text-white ring-4 ring-amber-500/20"
                        : "bg-gray-800 text-gray-500"
                    }`}
                  >
                    {step > i + 1 ? <i className="ri-check-line text-xs"></i> : s.id}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 h-0.5 mx-1">
                      <div
                        className="h-full bg-amber-500 transition-all duration-500 rounded-full"
                        style={{ width: step > i + 1 ? "100%" : "0%" }}
                      ></div>
                      <div className="h-full bg-gray-800 rounded-full -mt-0.5"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Intro card */}
        {step === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 md:p-10 text-center">
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { icon: "ri-home-2-line", label: "Home Size", color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
                { icon: "ri-thunderstorms-line", label: "Outage Frequency", color: "text-sky-400 bg-sky-500/10 border-sky-500/20" },
                { icon: "ri-coin-line", label: "Your Budget", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
              ].map((item) => (
                <div key={item.label} className={`border rounded-xl p-4 flex flex-col items-center gap-2 ${item.color}`}>
                  <div className="w-10 h-10 flex items-center justify-center text-2xl">
                    <i className={item.icon}></i>
                  </div>
                  <span className="text-xs font-semibold text-gray-300">{item.label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={() => setStep(1)}
              className="bg-amber-500 hover:bg-amber-400 text-white font-bold text-base px-10 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              Start the Quiz
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </button>
            <p className="text-gray-500 text-xs mt-3">Takes about 30 seconds — no email required</p>
          </div>
        )}

        {/* Question steps */}
        {step >= 1 && step <= 3 && currentStep && (
          <div
            className={`transition-all duration-280 ${animating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
          >
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 md:p-8">
              <div className="mb-2">
                <span className="text-amber-500 text-xs font-bold tracking-widest uppercase">{currentStep.eyebrow}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-extrabold text-white mb-1">{currentStep.question}</h3>
              <p className="text-gray-400 text-sm mb-6">{currentStep.sub}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentStep.options.map((opt) => {
                  const isSelected = answers[currentStep.key] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(currentStep.key, opt.value)}
                      className={`group flex items-start gap-4 p-4 rounded-xl border-2 text-left transition-all duration-200 cursor-pointer ${
                        isSelected
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-gray-800 bg-gray-950 hover:border-amber-500/50 hover:bg-gray-800"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 transition-colors ${
                          isSelected ? "bg-amber-500 text-white" : "bg-gray-800 text-gray-400 group-hover:bg-amber-500/20 group-hover:text-amber-400"
                        }`}
                      >
                        <i className={`${opt.icon} text-xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-bold text-sm transition-colors ${isSelected ? "text-amber-400" : "text-white"}`}>
                          {opt.label}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{opt.sub}</p>
                      </div>
                      {isSelected && (
                        <div className="w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-800">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                  {step === 1 ? "Start over" : "Back"}
                </button>
                <span className="text-gray-600 text-xs">{step} of 3</span>
              </div>
            </div>
          </div>
        )}

        {/* Result screen */}
        {step === 4 && recommendation && (
          <div className="animate-[fadeInUp_0.5s_ease_both]">
            <div className={`bg-gray-900 border-2 ${recommendation.accentColor} rounded-2xl overflow-hidden`}>
              {/* Result header */}
              <div className="bg-gradient-to-r from-amber-500/10 to-transparent px-6 md:px-8 py-5 border-b border-gray-800 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl shrink-0">
                  <i className={`${recommendation.icon} text-amber-400 text-2xl`}></i>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-amber-400 text-xs font-bold tracking-widest uppercase">Your Match</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-extrabold text-white leading-tight">{recommendation.title}</h3>
                  <p className="text-gray-400 text-xs mt-0.5">{recommendation.productType}</p>
                </div>
              </div>

              {/* Result body */}
              <div className="px-6 md:px-8 py-6">
                <p className="text-gray-300 text-sm leading-relaxed mb-6">{recommendation.desc}</p>

                {/* Answer summary */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    {
                      label: "Space",
                      value: steps[0].options.find((o) => o.value === answers.homeSize)?.label,
                      icon: steps[0].options.find((o) => o.value === answers.homeSize)?.icon,
                    },
                    {
                      label: "Outage Pattern",
                      value: steps[1].options.find((o) => o.value === answers.frequency)?.label,
                      icon: steps[1].options.find((o) => o.value === answers.frequency)?.icon,
                    },
                    {
                      label: "Budget",
                      value: steps[2].options.find((o) => o.value === answers.budget)?.label,
                      icon: steps[2].options.find((o) => o.value === answers.budget)?.icon,
                    },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-950 border border-gray-800 rounded-xl p-3 text-center">
                      <div className="w-7 h-7 flex items-center justify-center bg-gray-800 rounded-lg mx-auto mb-1.5">
                        <i className={`${item.icon} text-amber-400 text-sm`}></i>
                      </div>
                      <p className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold mb-0.5">{item.label}</p>
                      <p className="text-white text-xs font-semibold leading-tight">{item.value}</p>
                    </div>
                  ))}
                </div>

                {/* Brands */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  <span className="text-gray-500 text-xs font-semibold">Top picks from:</span>
                  {recommendation.brands.map((b) => (
                    <span key={b} className="bg-gray-800 border border-gray-700 text-gray-300 text-xs font-bold px-2.5 py-1 rounded-full">
                      {b}
                    </span>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => handleShop(recommendation)}
                    className="flex-1 flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shopping-bag-line"></i>
                    </div>
                    {recommendation.ctaLabel}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </button>
                  <button
                    onClick={() => { setStep(0); setAnswers({ homeSize: null, frequency: null, budget: null }); }}
                    className="flex items-center justify-center gap-1.5 border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-200 font-semibold text-sm px-5 py-3.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-refresh-line"></i>
                    </div>
                    Retake Quiz
                  </button>
                </div>

                <p className="text-gray-600 text-xs text-center mt-4">
                  Not sure? Call our experts at{" "}
                  <a href="tel:8666054975" className="text-amber-500 hover:text-amber-400 cursor-pointer transition-colors">
                    (866) 605-4975
                  </a>{" "}
                  — Mon–Fri, 9am–6pm EST
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
